import {memo, useMemo} from 'react'
import {Pressable, StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {LinearGradient} from 'expo-linear-gradient'
import {useLingui} from '@lingui/react/macro'

import {useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {LEVEL_LABELS} from '#/components/capsules/CapsulePostCard'
import {type HubCapsule} from '../hubPolicy'

const GRADIENTS: Array<[string, string, string]> = [
  ['#1e3a8a', '#7c3aed', '#db2777'],
  ['#0f766e', '#0891b2', '#2563eb'],
  ['#b45309', '#ea580c', '#dc2626'],
  ['#065f46', '#059669', '#0d9488'],
  ['#6d28d9', '#9333ea', '#ec4899'],
  ['#3730a3', '#4f46e5', '#06b6d4'],
]

function gradientFor(id: string): [string, string, string] {
  let hash = 0
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  return GRADIENTS[hash % GRADIENTS.length]
}

/** Dark base used behind video art in the feed cards. */
function blackColor(t: ReturnType<typeof useTheme>) {
  return t.atoms.bg_contrast_25.backgroundColor ?? '#000000'
}

export type CapsuleCardKind =
  | {kind: 'continue'}
  | {kind: 'boost'; pct: number}
  | {kind: 'history'; pct?: number}
  | {kind: 'draft'}
  | {kind: 'mostPlayed'; playCount: number}
  | {kind: 'published'}

export const CapsuleCard = memo(function CapsuleCard({
  capsule,
  kind,
  thumbnailUri,
  onPress,
}: {
  capsule: HubCapsule
  kind: CapsuleCardKind
  /** Video thumbnail of the capsule's post — the real card art. */
  thumbnailUri?: string
  onPress?: () => void
}) {
  const theme = useTheme()
  const {t: l} = useLingui()
  const gradient = useMemo(() => gradientFor(capsule.uri), [capsule.uri])

  // Art fallbacks: slide images when present, then a themed monogram.
  const slideArt = useMemo(() => {
    for (const slide of capsule.slides) {
      const raw = slide as {imageUrl?: string; videoThumbnailUrl?: string}
      if (raw.videoThumbnailUrl) return raw.videoThumbnailUrl
      if (raw.imageUrl) return raw.imageUrl
    }
    return undefined
  }, [capsule.slides])

  const artworkUri = thumbnailUri ?? capsule.coverImage ?? slideArt
  const black = blackColor(theme)
  const letterSource = (capsule.title || capsule.subject || '').trim()
  const letter = letterSource.charAt(0).toUpperCase() || '•'

  const levelLabel = useMemo(() => {
    if (!capsule.level) return undefined
    const desc = LEVEL_LABELS[capsule.level as string]
    return desc ? l(desc) : (capsule.level as string)
  }, [capsule.level, l])

  const badge = (() => {
    switch (kind.kind) {
      case 'continue':
        return {text: 'Continue', accent: '#22c55e' as const}
      case 'boost':
        return {text: `${kind.pct}% · Replay`, accent: '#f59e0b' as const}
      case 'history':
        return {
          text: kind.pct === undefined ? 'Played' : `${kind.pct}%`,
          accent: '#38bdf8' as const,
        }
      case 'draft':
        return {text: 'Draft', accent: '#a78bfa' as const}
      case 'mostPlayed':
        return {
          text: `${kind.playCount} ${kind.playCount === 1 ? 'play' : 'plays'}`,
          accent: '#f472b6' as const,
        }
      case 'published':
        return {text: 'Published', accent: '#34d399' as const}
    }
  })()

  const coinCount = capsule.slideCount ?? 0

  const title =
    capsule.title.trim().length > 0
      ? capsule.title
      : capsule.subject?.trim()
        ? `${capsule.subject}${levelLabel ? ` · ${levelLabel}` : ''}`
        : undefined

  const inner = (
    <View style={[styles.card, theme.atoms.border_contrast_low]}>
      {/* 9:16 video-art tile */}
      <View style={[styles.artwork, {backgroundColor: black}]}>
        {artworkUri ? (
          <Image
            source={{uri: artworkUri}}
            style={styles.image}
            contentFit="cover"
            accessibilityIgnoresInvertColors
            transition={150}
          />
        ) : (
          <LinearGradient colors={gradient} style={styles.image}>
            <View style={styles.artworkPlaceholder}>
              <Text
                style={styles.artworkLetter}
                numberOfLines={1}
                allowFontScaling={false}>
                {letter}
              </Text>
            </View>
          </LinearGradient>
        )}

        {/* Top-left: hub state pill */}
        <View style={[styles.topRow, styles.left]}>
          <View style={[styles.pill, {backgroundColor: badge.accent}]}>
            <Text style={styles.pillText} allowFontScaling={false}>
              {badge.text}
            </Text>
          </View>
        </View>

        {/* Top-right: coin reward, mirroring the feed capsule card */}
        {coinCount > 0 ? (
          <View style={[styles.topRow, styles.right]}>
            <View style={styles.coinPill}>
              <Text style={styles.coinIcon} allowFontScaling={false}>
                🪙
              </Text>
              <Text style={styles.coinText} allowFontScaling={false}>
                {coinCount}
              </Text>
            </View>
          </View>
        ) : null}

        {/* Bottom: level + subject over a scrim, mirroring the feed card */}
        {(levelLabel || capsule.subject) && (
          <View style={styles.bottomScrim}>
            <LinearGradient
              colors={[black, 'rgba(0, 0, 0, 0)']}
              locations={[0.02, 1]}
              start={{x: 0, y: 1}}
              end={{x: 0, y: 0}}
              style={styles.scrimGradient}
            />
            <View style={styles.bottomText}>
              {levelLabel ? (
                <Text
                  style={styles.levelText}
                  numberOfLines={1}
                  allowFontScaling={false}>
                  {levelLabel}
                </Text>
              ) : null}
              {capsule.subject ? (
                <Text
                  style={styles.subjectText}
                  numberOfLines={1}
                  allowFontScaling={false}>
                  {capsule.subject}
                </Text>
              ) : null}
            </View>
          </View>
        )}
      </View>

      {title ? (
        <View style={styles.meta}>
          <Text style={styles.title} numberOfLines={2}>
            {title}
          </Text>
        </View>
      ) : null}
    </View>
  )

  if (!onPress) return inner
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={title ?? badge.text}
      accessibilityHint="Opens this capsule"
      style={({pressed}) => [pressed && styles.pressed]}>
      {inner}
    </Pressable>
  )
})

const styles = StyleSheet.create({
  card: {
    width: 150,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  artwork: {
    aspectRatio: 9 / 16,
    width: '100%',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  artworkPlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  artworkLetter: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 44,
    fontWeight: '800',
  },
  topRow: {
    position: 'absolute',
    top: 6,
    flexDirection: 'row',
  },
  left: {
    left: 6,
  },
  right: {
    right: 6,
  },
  pill: {
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
  },
  pillText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.2,
  },
  coinPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    backgroundColor: 'rgba(0,0,0,0.55)',
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 3,
  },
  coinIcon: {
    fontSize: 10,
  },
  coinText: {
    color: '#FFD700',
    fontSize: 10,
    fontWeight: '800',
    lineHeight: 13,
  },
  bottomScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '46%',
    justifyContent: 'flex-end',
  },
  scrimGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  bottomText: {
    paddingHorizontal: 8,
    paddingBottom: 8,
    gap: 1,
  },
  levelText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '800',
  },
  subjectText: {
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: 10,
  },
  meta: {
    padding: 8,
    paddingBottom: 6,
  },
  title: {
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 16,
  },
  pressed: {
    opacity: 0.8,
  },
})
